import { DownloadOutlined } from '@ant-design/icons';
import { Button, message, Select, Spin, Col, Row } from 'antd';
import { saveAs } from 'file-saver';
import { Formik } from 'formik';
import { Input, Form, SubmitButton } from 'formik-antd';
import { orderBy } from 'lodash';
import moment from 'moment';
import { resetHospital, setItemInvoice, updateHospitalSlice } from 'pages/BillingProcess/slices';
import { FC, useCallback, useEffect } from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { exportFile } from 'utils/export.api';
import { ButtonCSVDetail, ButtonCSVRegister, Pagination, TableBillingFirstStep } from 'components';
import { billingProcessService } from 'services';
import { t_medical_institution, hospital_visit_and_links_results } from 'types';
import { billingSelectors, workSpaceSelectors } from '../../../containers/App/selectors';
import {
  checkBill,
  importInvoice,
  getHospitalForPage,
  registerNewBill,
  updateHospital,
  getHospitalExport,
  getAllCPAPExport,
  getResultImport,
  removeHospital,
} from '../../BillingProcess/thunk';
import ButtonSelectAll from './ButtonSelectAll';
import { Wrapp } from './styles';

type Props = {
  nextStep: () => void;
};

const defaultOptionInvoice = [
  {
    name: moment().subtract(1, 'M').format('YYYY年MM月度'),
    value: moment().subtract(1, 'M').format('YYYYMM'),
  },
  {
    name: moment().format('YYYY年MM月度'),
    value: moment().format('YYYYMM'),
  },
];

const BillingFirstStep: FC<Props> = ({ nextStep }) => {
  const dispatch = useDispatch();
  const location = useLocation<{ medicalDetail: t_medical_institution }>();
  const { hospital, optionInvoice, fieldHospital, itemInvoice, makeTrueConsultationLoding, consultationErrorStatus } =
    useSelector(billingSelectors);
  const { statusBill } = useSelector(workSpaceSelectors);
  const [page, setPage] = useState<number>(1);
  const [loadHospital, setLoadHospital] = useState(false);
  const [loadExportExcel, setLoadExportExcel] = useState(false);
  const [isD2, setIsD2] = useState(true);
  const [invoiceMonth, setInvoiceMonth] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [resettingSearchForm, setResettingSearchForm] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const dateNewFormat = [moment().subtract(1, 'M').format('YYYYMM'), moment().format('YYYYMM')];

      await dispatch(checkBill({ facility_id: location.state.medicalDetail.i_id || '', invoice_month: dateNewFormat }));
      setLoading(false);
    })();
  }, [location.state.medicalDetail.i_id, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetHospital());
    };
  }, [dispatch]);

  const registerBill = async () => {
    setLoading(true);
    if (statusBill?.value === invoiceMonth) {
      const invoice = optionInvoice.find(({ value }) => value === invoiceMonth);
      if (invoice) {
        dispatch(setItemInvoice(invoice));
        setLoadHospital(true);
        setIsD2(false);
        await dispatch(
          getHospitalForPage({
            facility_id: location.state.medicalDetail.i_id || '',
            invoice_month: invoiceMonth,
            page: page,
            per_page: 50,
          }),
        );
        setLoadHospital(false);
      } else {
        setError('請求書が作成されていません');
      }
      setLoading(false);
    } else {
      const bill: any = await dispatch(
        registerNewBill({
          facility_id: location.state.medicalDetail.i_id || '',
          invoice_month: invoiceMonth,
          facility_name: location.state.medicalDetail.facility_name ?? '',
        }),
      );
      if ('payload' in bill) {
        const Status = bill.payload.items[0].Status;
        if (Status === '入金済み' || Status === 'already deposited' || Status === '終了' || Status === 'Closed') {
          setError('指定した請求書は編集できません');
          return;
        }
        setError(null);
      }
      setLoadHospital(true);
      const result: any = await dispatch(
        importInvoice({
          facility_id: location.state.medicalDetail.i_id || '',
          invoice_month: invoiceMonth,
          facilityId: location.state.medicalDetail.facility_id || '',
        }),
      );
      if (!result.payload || result.payload.result) {
        setIsD2(false);
        await dispatch(
          getHospitalForPage({
            facility_id: location.state.medicalDetail.i_id || '',
            invoice_month: invoiceMonth,
            page: page,
            per_page: 50,
          }),
        );
        setLoadHospital(false);
      } else if (result.payload.error) {
        setIsD2(false);
        message.error('データインポート中にエラーが発生しました。処理を中断し、システム管理会社にご連絡ください。', 5);
        await dispatch(
          getHospitalForPage({
            facility_id: location.state.medicalDetail.i_id || '',
            invoice_month: invoiceMonth,
            page: page,
            per_page: 50,
          }),
        );
        setLoadHospital(false);
      } else {
        const getStatusImport = async (stream_id: string) => {
          const resultImport: any = await dispatch(
            getResultImport({
              stream_id: stream_id,
            }),
          );
          const obj = resultImport ? resultImport.payload : null;
          if (obj) {
            const status = obj[Object.keys(obj)[0]].status;

            if (status.id === 0 || status.id === 1) {
              setTimeout(() => getStatusImport(stream_id), 5000);
            } else if (status.id === 2) {
              setIsD2(false);
              await dispatch(
                getHospitalForPage({
                  facility_id: location.state.medicalDetail.i_id || '',
                  invoice_month: invoiceMonth,
                  page: page,
                  per_page: 50,
                }),
              );
              setLoadHospital(false);
            } else {
              setIsD2(false);
              message.error(
                'データインポート中にエラーが発生しました。処理を中断し、システム管理会社にご連絡ください。',
                5,
              );
              await dispatch(
                getHospitalForPage({
                  facility_id: location.state.medicalDetail.i_id || '',
                  invoice_month: invoiceMonth,
                  page: page,
                  per_page: 50,
                }),
              );
              setLoadHospital(false);
            }
          }
        };
        getStatusImport(result.payload.stream_id);
      }
      setLoading(false);
    }
  };

  const handleChangePage = async (page: number) => {
    setPage(page);
    setLoadHospital(true);
    await dispatch(
      getHospitalForPage({
        facility_id: location.state.medicalDetail.i_id || '',
        invoice_month: invoiceMonth,
        page: page,
        per_page: 50,
      }),
    );
    setLoadHospital(false);
  };

  const updateAfterUpload = async () => {
    await dispatch(
      getHospitalForPage({
        facility_id: location.state.medicalDetail.i_id || '',
        invoice_month: invoiceMonth,
        page: page,
        per_page: 50,
      }),
    );
  };

  const handleChangeData = async (
    value: string | boolean,
    row: hospital_visit_and_links_results,
    keyField: string,
    index: number,
  ) => {
    let optionFieldHospital = [];
    let optionFieldBillType = [];
    let billingTypeDefault = '';
    let newHos: any = {};
    const result = fieldHospital?.find(([_key, value]: [string, any]) => value.display_id === 'hospital_visit');
    if (result) {
      optionFieldHospital = result[1].options;
    }
    const resultBill = fieldHospital?.find(([_key, value]: [string, any]) => value.display_id === 'billing_type');
    if (resultBill) {
      optionFieldBillType = resultBill[1].options;
    }
    if (keyField === 'hospital_visit') {
      billingTypeDefault = value === 'o_yes' ? 'o_normal' : 'o_unpaid';
      dispatch(
        updateHospital({
          invoice_month: invoiceMonth,
          hospital: {
            i_id: row.item_ref?.csv_key.i_id,
            [keyField]: value.toString(),
            billing_type: billingTypeDefault,
          },
        }),
      );
      const hospitalValue = optionFieldHospital.find((f: any) => f.display_id === value);
      const billingTypeValue = optionFieldBillType.find((f: any) => f.display_id === billingTypeDefault);
      newHos = {
        ...row,
        hospital_visit: hospitalValue && hospitalValue.value,
        billing_type: billingTypeValue && billingTypeValue.value,
      };
      updateCandidateBillingType(row, keyField, value.toString());
    } else if (keyField === 'billing_type') {
      const biilingTypeValue = optionFieldBillType.find((f: any) => f.display_id === value);
      dispatch(
        updateHospital({
          hospital: { i_id: row.item_ref?.csv_key.i_id, [keyField]: value.toString() },
          invoice_month: invoiceMonth,
        }),
      );
      newHos = { ...row, [keyField]: biilingTypeValue && biilingTypeValue.value };
      updateCandidateBillingType(row, keyField, value.toString());
    } else {
      dispatch(
        updateHospital({
          invoice_month: invoiceMonth,
          hospital: { i_id: row.item_ref?.csv_key.i_id, [keyField]: value },
        }),
      );
      newHos = { ...row, [keyField]: value };
    }
    const newState = [...hospital.items];
    newState.splice(index, 1, newHos);
    dispatch(
      updateHospitalSlice({
        hospital: newState,
        total: hospital.totalItems,
      }),
    );
  };

  const updateCandidateBillingType = async (
    row: hospital_visit_and_links_results,
    key_field: string,
    value: string,
  ) => {
    let searchBillType = '';
    let updateBillType = '';
    let invoiceMonth = '';
    if (key_field === 'hospital_visit') {
      searchBillType = value == 'o_yes' ? 'o_unpaid' : 'o_unpaid_collection';
      updateBillType = value == 'o_yes' ? 'o_unpaid_collection' : 'o_unpaid';
      invoiceMonth = value == 'o_yes' ? row.target_month : '';
    } else if (key_field === 'billing_type') {
      const hospital_visit = row.id_value_results['hospital_visit']['id'];
      if (hospital_visit === 'o_no') {
        return;
      }
      if (value === 'o_normal_past_no_claim') {
        searchBillType = 'o_unpaid_collection';
        updateBillType = 'o_unpaid';
        invoiceMonth = '';
      } else {
        if (row.billing_type === '受診（未受診分請求なし）' || row.billing_type === 'o_normal_past_no_claim') {
          searchBillType = 'o_unpaid';
          updateBillType = 'o_unpaid_collection';
          invoiceMonth = row.target_month;
        } else {
          return;
        }
      }
    } else {
      return;
    }
    const candidate: any = await billingProcessService.getBillingCandidate({
      conditions: [
        {
          id: 'billing_type',
          search_value: [searchBillType],
        },
        {
          id: 'cpap_id',
          search_value: [row.cpap_id],
          exact_match: true,
        },
      ],
    });
    if (candidate.data.report_results.length > 0) {
      const candidateCpap = candidate.data.report_results.map((v: any) => v.item_ref.hospital_visit.i_id);
      for (let i = 0; i < candidateCpap.length; i++) {
        billingProcessService.updateHospital({
          i_id: candidateCpap[i],
          billing_type: updateBillType,
          invoice_month: invoiceMonth,
          invoice_id: '',
        });
      }
    }
  };

  const handleUpdate = () => {
    nextStep();
  };

  const exportCSV = useCallback(async () => {
    const data: any = await dispatch(
      getHospitalExport({
        facility_id: location.state.medicalDetail.i_id || '',
        invoice_month: invoiceMonth,
        facilityId: location.state.medicalDetail.facility_name || '',
      }),
    );
    const listCsv =
      data.payload.report_results.length > 0
        ? data.payload.report_results.map((item: hospital_visit_and_links_results) => {
            return {
              patient_id: item.patient_id ?? '',
              patient_name: item.patient_name ?? '',
              hospital_visit: item.hospital_visit ?? '',
              hospital_visit_remark: item.hospital_visit_remark ?? '',
              billing_type: item.billing_type ?? '',
              csv_key: item.csv_key ?? '',
            };
          })
        : [];
    const csvString = [
      ['患者ID', 'ご利用者名', '受診○☓', '備考', '請求処理タイプ', 'csv_key'],
      ...listCsv.map((item: any) => Object.values(item)),
    ]
      .map((e) => e.join(','))
      .join('\n');
    const bom = '\uFEFF';
    const file = new Blob([bom, csvString], { type: 'application/octet-stream' });
    saveAs(
      file,
      `受診明細アップロードCSV_${
        data.payload.report_results.length > 0 ? data.payload.report_results[0].facility_id : ''
      }_${data?.payload?.length > 0 ? data.payload.report_results[0].target_month : ''}_${moment().format(
        'YMMDDHmm',
      )}.csv`,
    );
  }, [dispatch, location, invoiceMonth]);

  const exportExcel = useCallback(async () => {
    try {
      setLoadExportExcel(true);
      const dateNew = `${itemInvoice?.invoice_month?.slice(0, 4)}年${itemInvoice?.invoice_month?.slice(4)}月度`;
      const result: any = await dispatch(getAllCPAPExport(location.state.medicalDetail.i_id ?? ''));

      const pre_patients =
        result.payload.length > 0
          ? result.payload.map((r: any) => {
              return {
                patient_name: r.patient_name ?? '',
                remark_for_output: r.remark_for_output ?? '',
                patient_id: r.patient_id ?? '',
                sort_num: r.sort_num ? Number(r.sort_num) : 99999,
                created_at: r.created_at,
              };
            })
          : [];
      const patients =
        pre_patients.length > 0
          ? orderBy(pre_patients, ['sort_num', 'created_at'], ['asc', 'asc']).map((r: any, i: number) => {
              r.index = i + 1;
              return r;
            })
          : [];
      const objExport = {
        facility_name: location.state.medicalDetail.facility_name,
        facility_code: location.state.medicalDetail.facility_code,
        invoice_month: dateNew + 'CPAP患者様受診確認',
        patients: patients,
        in_charge_of_sales: location.state.medicalDetail?.in_charge_of_sales,
      };
      await exportFile(
        objExport,
        'xlsx',
        'ExcelHexabase',
        `受診明細_${location.state.medicalDetail.facility_name}_${itemInvoice?.invoice_month?.slice(
          0,
          4,
        )}${itemInvoice?.invoice_month?.slice(4)}`,
      );
    } finally {
      setLoadExportExcel(false);
    }
  }, [dispatch, location.state, itemInvoice]);

  const handleRemoveHospital = async (row: hospital_visit_and_links_results, index: number) => {
    setLoadHospital(true);
    const resultAction: any = await dispatch(removeHospital({ i_id: row.item_ref?.csv_key?.i_id }));
    if (removeHospital.fulfilled.match(resultAction)) {
      const newState = [...hospital.items];
      newState.splice(index, 1, { ...row, billing_type: '', hospital_visit: '', invoice_month: '' });
      dispatch(
        updateHospitalSlice({
          hospital: newState,
          total: hospital.totalItems,
        }),
      );
    }
    setLoadHospital(false);
  };

  const handleSearch = async (values: { value: string }) => {
    if (!values.value) return;
    await dispatch(
      getHospitalForPage({
        facility_id: location.state.medicalDetail.i_id || '',
        invoice_month: invoiceMonth,
        page: page,
        per_page: 50,
        conditions: [
          {
            conditions: [
              { id: 'patient_name', search_value: [values.value] },
              { id: 'patient_id', search_value: [values.value] },
            ],
            use_or_condition: true,
          },
        ],
      }),
    );
  };

  const handleResetSearch = async () => {
    setResettingSearchForm(true);
    await dispatch(
      getHospitalForPage({
        facility_id: location.state.medicalDetail.i_id || '',
        invoice_month: invoiceMonth,
        page: page,
        per_page: 50,
      }),
    );
    setResettingSearchForm(false);
  };

  if (isD2) {
    return (
      <Spin spinning={loadHospital} size="large">
        <Wrapp>
          <div className="section">
            <div>作成・編集を行う請求月度を選択し開始ボタンをクリックしてください。</div>
            <Select
              style={{ width: '20%' }}
              value={invoiceMonth}
              disabled={loading}
              onChange={(e) => {
                setError('');
                setInvoiceMonth(e);
              }}
            >
              {optionInvoice.length > 0
                ? optionInvoice.map((op) => <Select.Option value={op.value}>{op.name}</Select.Option>)
                : defaultOptionInvoice.map((opd) => <Select.Option value={opd.value}>{opd.name}</Select.Option>)}
            </Select>
            <Button
              disabled={!invoiceMonth}
              id="btn-small"
              className="btn-save-step"
              onClick={registerBill}
              loading={loading}
            >
              開始
            </Button>
            <div className="bill-error">{error}</div>
          </div>
        </Wrapp>
      </Spin>
    );
  }

  return (
    <Spin spinning={loadHospital || makeTrueConsultationLoding} size="large">
      <Wrapp>
        <div className="section">
          <Row>
            <Col className="wn-error" span={12}>
              {itemInvoice &&
                (itemInvoice.Status === '請求書発行済' ||
                  itemInvoice.Status === 'invoice issued' ||
                  itemInvoice.Status === '請求書登録済' ||
                  itemInvoice.Status === 'Invoicing completed') &&
                '受診明細と請求金額に不整合が発生するため、編集を行った際は必ず「登録完了」まで進めて請求書を上書きしてください。'}
              {itemInvoice && statusBill?.value === invoiceMonth ? <br /> : null}
              {statusBill?.value === invoiceMonth
                ? `${invoiceMonth.slice(0, 4)}年${invoiceMonth.slice(4)}月度の請求書は確定しました。`
                : ''}
            </Col>
            <Col span={12}>
              {statusBill?.value !== invoiceMonth ? (
                <div className="btn-modal">
                  <ButtonSelectAll
                    facility_id={location.state.medicalDetail.i_id}
                    invoice_month={invoiceMonth}
                    page={page}
                  >
                    全て◯をつける
                  </ButtonSelectAll>
                  <ButtonCSVDetail action={exportExcel} text="受診明細CSV出力" loading={loadExportExcel} />
                  <ButtonCSVRegister updateHospital={updateAfterUpload} text="受診状況をCSVで登録" />
                  <span onClick={exportCSV}>
                    <DownloadOutlined /> <u>登録用CSVをダウンロード</u>
                  </span>
                </div>
              ) : null}
            </Col>
          </Row>
          {consultationErrorStatus && (
            <p className="consultation-error">エラーが発生しました。再度実行いただくか、1件ずつ入力してください。</p>
          )}
          <div className="header-degree-registration">
            <p className="extraLargeFont">
              1. {`${invoiceMonth.slice(0, 4)}年${invoiceMonth.slice(4)}月`}度の受診状況登録
            </p>
            <Formik
              onSubmit={handleSearch}
              onReset={handleResetSearch}
              initialValues={{
                value: '',
              }}
            >
              {({ isSubmitting }) => (
                <Form className="search-form">
                  <Form.Item name="value">
                    <Input className="input-search" name="value" placeholder="患者名・患者IDで検索" />
                  </Form.Item>
                  <SubmitButton className="submit-button" disabled={resettingSearchForm}>
                    絞込
                  </SubmitButton>
                  <Button
                    htmlType="reset"
                    className="reset-button"
                    loading={resettingSearchForm}
                    disabled={isSubmitting}
                  >
                    リセット
                  </Button>
                </Form>
              )}
            </Formik>
          </div>
          <div className="table-billing">
            <TableBillingFirstStep
              data={hospital ? hospital.items : []}
              onChangeData={handleChangeData}
              handleRemoveHospital={handleRemoveHospital}
              fieldHospital={fieldHospital}
              page={page}
            />
          </div>
          <div style={{ marginTop: '20px' }}>
            <Pagination
              page={page}
              onChangePage={handleChangePage}
              totalItems={hospital ? hospital.totalItems : 0}
              per_page={50}
            />
          </div>
          <div className="wrap-button">
            <Button
              disabled={invoiceMonth === moment().format('YYYYMM')}
              onClick={handleUpdate}
              className={`btn-save-step${invoiceMonth === moment().format('YYYYMM') ? ' disabled' : ''}`}
            >
              {statusBill?.value === invoiceMonth ? '次へ' : '保存して次へ'}
            </Button>
          </div>
        </div>
      </Wrapp>
    </Spin>
  );
};
export default BillingFirstStep;
